import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './styles/index.css'
import { scheduleRecurringChecks } from '@/features/comunicacao/services/notificationsService'
import { supabase } from '@features/sistema/services/supabaseClient'

// Dev mobile console (eruda) only on Android/iPhone in development
if (import.meta.env.DEV && /Android|iPhone/i.test(navigator.userAgent)) {
  const s = document.createElement('script')
  s.src = 'https://cdn.jsdelivr.net/npm/eruda'
  s.onload = () => { (window as any).eruda?.init() }
  document.body.appendChild(s)
}

// iOS Safari viewport fix: mantém --vh e --app-height alinhados ao visualViewport
(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const vv: any = (window as any).visualViewport;

  const update = () => {
    const h = vv?.height ?? window.innerHeight;
    const vh = h * 0.01;
    // --vh para uso fino e fallback quando 100dvh não for confiável
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    // --app-height passa a refletir o visual viewport em tempo real
    document.documentElement.style.setProperty('--app-height', `calc(var(--vh) * 100)`);
  };

  let raf = 0;
  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  };

  update();
  vv?.addEventListener?.('geometrychange', schedule);
  vv?.addEventListener?.('resize', schedule);
  vv?.addEventListener?.('scroll', schedule);
  window.addEventListener('orientationchange', schedule);
  window.addEventListener('pageshow', schedule);
})();

// reload único quando o SW assumir controle (fallback)
let __pwaReloaded = false;
navigator.serviceWorker?.addEventListener?.('controllerchange', () => {
  if (__pwaReloaded) return;
  if (!(window as any).__pwaUserInitiatedReload) return; // só se foi via botão
  __pwaReloaded = true;
  location.reload();
});

// listener global para mensagem RELOAD_WINDOW do service worker
navigator.serviceWorker?.addEventListener?.('message', (e: MessageEvent) => {
  if (e.data?.type === 'RELOAD_WINDOW' && !__pwaReloaded) {
    __pwaReloaded = true;
    location.reload();
  }
});

// ✅ FUNÇÃO DE INICIALIZAÇÃO DOS SERVIÇOS OFFLINE - VERSÃO MÍNIMA
async function initializeOfflineServices() {
  try {
    const { cacheService } = await import('@features/sistema/services/cacheService');
    console.log('🚀 ACOR ERP: Inicializando serviços offline...')
    await cacheService.init()
    if (Notification.permission === 'granted') scheduleRecurringChecks()
    await cacheService.cleanOldCache(168)
  } catch (err) {
    console.error('Falha ao iniciar serviços offline', err)
  }
}

// 🔥 WARM-UP OFFLINE: pré-carrega rotas/módulos críticos em idle (se online)
const warmupOfflineAssets = () => {
  if (!navigator.onLine) return;
  const idle = (cb: () => void) =>
    ('requestIdleCallback' in window) ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 0);
  idle(async () => {
    try {
      await Promise.allSettled([
        // rotas (garantem primeira abertura offline)
        import('@/features/sistema/pages/Home'),
        import('@/features/sistema/pages/Obras'),
        import('@/features/sistema/pages/ObraDetalhes'),
        import('@/features/tarefas/pages/MinhasTarefas'),
        import('@/features/diario/pages/DiariosList'),
        import('@/features/checklist/pages/ChecklistList'),
        import('@/features/cronograma/pages/Cronograma'),
        // 🤖 nova página do Assistente
        import('@/features/ia/pages/AssistentePage.tsx'),
        // serviços necessários com frequência
        import('@features/sistema/services/cacheService'),
      ]);
    } catch (e) {
      // silencioso: warm-up é "best effort"
    }
  });
};

// ✅ NOVO: PRÉ-CARREGA DADOS ESSENCIAIS PARA MODO OFFLINE
const prefetchOfflineData = async () => {
  if (!navigator.onLine) return;

  const idle = (cb: () => void) =>
    ('requestIdleCallback' in window) ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 2000);

  idle(async () => {
    try {
      console.log('📦 ACOR ERP: Pré-carregando dados para modo offline...');

      const { cacheService } = await import('@features/sistema/services/cacheService');

      await cacheService.init();

      // ✅ Verificar se já há dados recentes no cache (menos de 5 minutos)
      const status = await cacheService.getCacheStatus();
      if (status.obras > 0 && status.tarefas > 0) {
        console.log(`📦 ACOR ERP: Cache já possui dados (${status.obras} obras, ${status.tarefas} tarefas)`);
        return; // Não precisa pré-carregar novamente
      }

      // ✅ Buscar obras
      const { data: obrasData } = await supabase
        .from('obras')
        .select('id, nome, status, foto_capa, is_personal, tipo_obra, supervisor_id')
        .order('nome');

      if (obrasData && obrasData.length > 0) {
        await cacheService.cacheObras(obrasData);
        console.log(`📦 ACOR ERP: ${obrasData.length} obras pré-carregadas`);

        // ✅ Buscar tarefas de cada obra (em paralelo, com limite)
        const obrasAtivas = obrasData.filter(o => o.status !== 'concluida').slice(0, 10);
        const tarefasPromises = obrasAtivas.map(async (obra) => {
          try {
            const { data: tarefasData } = await supabase
              .from('tarefas')
              .select('*')
              .eq('obra_id', obra.id)
              .eq('ativo', true)
              .order('data_inicio', { ascending: true, nullsFirst: false });

            if (tarefasData && tarefasData.length > 0) {
              await cacheService.cacheTarefas(tarefasData);
              return tarefasData.length;
            }
            return 0;
          } catch {
            return 0;
          }
        });

        const resultados = await Promise.all(tarefasPromises);
        const totalTarefas = resultados.reduce((acc, curr) => acc + curr, 0);
        console.log(`📦 ACOR ERP: ${totalTarefas} tarefas pré-carregadas de ${obrasAtivas.length} obras`);
      }

      console.log('✅ ACOR ERP: Pré-carregamento offline concluído');
    } catch (err) {
      console.warn('⚠️ ACOR ERP: Erro no pré-carregamento (não crítico):', err);
    }
  });
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)

// Boot
initializeOfflineServices();
warmupOfflineAssets();
prefetchOfflineData(); // ✅ NOVO: Pré-carrega dados essenciais para modo offline